import { lazy, Suspense, type JSX, useEffect, useState } from 'react';

import {
  SELF_PAY_GROUP_ID,
  SELF_PAY_PAYER_ID,
  SUPPORTED_PAYERS,
} from '../config/supported-payers.js';
import { BookingStep } from '../features/booking/booking-step.js';
import { EligibilityForm } from '../features/eligibility/eligibility-form.js';
import { EligibilityOutcome } from '../features/eligibility/eligibility-outcome.js';
import { EnrollmentOutcome } from '../features/eligibility/enrollment-outcome.js';
import { ExistingMemberSupport } from '../features/eligibility/existing-member-support.js';
import {
  checkEligibility,
  finalizeSetupIntent,
  requiresPayment,
  routeEnrollment,
  type EligibilityCheckInput,
  type EligibilityCheckResult,
  type EnrollmentResult,
} from '../lib/ffs-api.js';
import {
  clearPaymentResume,
  loadPaymentResume,
  readStripeRedirectStatus,
} from '../lib/payment-resume.js';

// Lazy-loaded: `payment-step.js` pulls in `../../lib/stripe.js` (Stripe Elements), which must NOT
// end up in the main bundle — the covered path (most members) never touches Stripe at all, and a
// misconfigured/missing publishable key must not risk affecting the non-payment bundle.
const PaymentStep = lazy(() =>
  import('../features/payment/payment-step.js').then((m) => ({ default: m.PaymentStep })),
);

/** Eligibility check (Story 1.2 UI) + enrollment CTA (Story 1.3 UI): form → outcome → enrollment. */
export default function EligibilityPage(): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<EligibilityCheckResult | null>(null);
  const [lastInput, setLastInput] = useState<EligibilityCheckInput | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollResult, setEnrollResult] = useState<EnrollmentResult | null>(null);
  const [booked, setBooked] = useState(false);
  const [paid, setPaid] = useState(false);

  async function run(input: EligibilityCheckInput): Promise<void> {
    setSubmitting(true);
    setLastInput(input);
    const r = await checkEligibility(input);
    setResult(r);
    setSubmitting(false);
  }

  function retry(): void {
    if (lastInput) void run(lastInput);
    else setResult(null);
  }

  /**
   * "Continue without insurance", offered on the payer_unavailable (503) and member_not_found
   * screens. Re-runs the SAME person as self-pay rather than enrolling them directly.
   *
   * Enrolling straight from those states would carry the insurance payerId/memberId into
   * routeEnrollment against a plan that was never verified, and `requiresPayment()` is false for
   * payer_unavailable — so the member would reach booking with no card captured and a self-pay
   * consult billed to nobody. Re-checking as self-pay produces a `self_pay` result, which carries
   * the server-owned price, makes requiresPayment() true, and reuses the payment path that already
   * works for members who chose self-pay on the form.
   *
   * memberId is OMITTED, not set to undefined: the eligibility-check schema is `.strict()` and the
   * repo builds with exactOptionalPropertyTypes.
   *
   * Takes the input as a PARAMETER rather than reading `lastInput` and guarding on null. The outcome
   * view only renders when both `result` and `lastInput` are set (they are written together, by
   * `run()` and by the 3DS resume effect), so a null guard here would be unreachable — dead code
   * that no test can honestly cover. The narrowing lives in the render condition instead.
   */
  function continueWithoutInsurance(input: EligibilityCheckInput): void {
    const { memberId: _discarded, ...person } = input;
    void run({ ...person, payerId: SELF_PAY_PAYER_ID, groupId: SELF_PAY_GROUP_ID });
  }

  async function enroll(): Promise<void> {
    if (!lastInput || enrolling) return;
    setEnrolling(true);
    const r = await routeEnrollment(lastInput);
    setEnrolling(false);
    setEnrollResult(r);
  }

  // 3-D Secure resume: a card that required full-page 3DS navigated away during confirmSetup and
  // Stripe returned here with `redirect_status`. Restore the funnel from the sessionStorage blob
  // PaymentStep saved, so the member lands back on booking (succeeded) or the payment retry
  // (otherwise) instead of a reset form. Runs once on mount.
  useEffect(() => {
    const status = readStripeRedirectStatus();
    const resume = loadPaymentResume();
    if (status === null || resume === null) return;
    // Strip Stripe's query params so a reload doesn't re-trigger this.
    window.history.replaceState({}, document.title, window.location.pathname);
    clearPaymentResume();
    setLastInput(resume.member);
    setResult(resume.result);
    setEnrollResult({
      kind: 'enrolled',
      bookingUrl: resume.bookingUrl,
      patientId: resume.patientId,
    });
    if (status === 'succeeded') {
      setPaid(true);
      void finalizeSetupIntent({
        inboxHealthIntentId: resume.inboxHealthIntentId,
        patientId: resume.patientId,
      }).catch(() => {
        // Finalize failure is non-fatal to the member's flow (the card was confirmed with Stripe);
        // they proceed to booking. Backend instrumentation/alarms cover the finalize gap.
      });
    }
    // else: 3DS not completed → paid stays false → PaymentStep re-renders for a retry.
  }, []);

  // Card capture (Story 7/8) sits between enrollment and booking, but ONLY for members who owe
  // money (copay or out-of-pocket) — the covered path goes straight to booking, unaffected.
  const isEnrolled = enrollResult?.kind === 'enrolled';
  const needsPayment = isEnrolled && result !== null && requiresPayment(result);
  const canCapturePayment =
    enrollResult?.kind === 'enrolled' && enrollResult.patientId !== undefined;
  if (isEnrolled && needsPayment && !canCapturePayment) {
    // Enrolled result carries no patientId to key the setup intent on — don't block the funnel on
    // it, just skip straight to booking (this shouldn't happen: routeEnrollment always returns a
    // patientId alongside `enrolled`).
    console.warn(
      '[ffs-signup] enrolled + requiresPayment but no patientId — skipping payment step',
    );
  }
  const showPayment = isEnrolled && needsPayment && canCapturePayment && !paid;
  const showBooking = isEnrolled && !showPayment;

  let view: JSX.Element;
  if (showBooking && enrollResult?.kind === 'enrolled' && lastInput !== null) {
    view = (
      <BookingStep
        bookingUrl={enrollResult.bookingUrl}
        member={lastInput}
        onBooked={() => setBooked(true)}
        {...(enrollResult.patientId !== undefined ? { patientId: enrollResult.patientId } : {})}
      />
    );
  } else if (
    showPayment &&
    enrollResult?.kind === 'enrolled' &&
    enrollResult.patientId !== undefined &&
    lastInput !== null &&
    result !== null
  ) {
    view = (
      <Suspense fallback={<p className="text-lg text-[var(--ink-soft)]">Loading payment form…</p>}>
        <PaymentStep
          patientId={enrollResult.patientId}
          bookingUrl={enrollResult.bookingUrl}
          member={lastInput}
          result={result}
          onPaid={() => setPaid(true)}
        />
      </Suspense>
    );
  } else if (result !== null && result.kind === 'existing_member') {
    // Terminal cross-program block — checked before the enroll/enrollment-outcome branches so an
    // existing_member result can never reach enrollment, even in principle.
    view = <ExistingMemberSupport onReset={() => setResult(null)} />;
  } else if (enrolling || enrollResult !== null) {
    view = (
      <EnrollmentOutcome
        result={enrolling ? 'enrolling' : (enrollResult as EnrollmentResult)}
        onRetry={() => void enroll()}
      />
    );
  } else if (result !== null && lastInput !== null) {
    // `lastInput !== null` is not defensive padding: it NARROWS the state the outcome's self-pay
    // handler needs, so that handler carries no unreachable null branch. The two are always written
    // together (run() and the 3DS resume effect), so this cannot change what a member sees; if the
    // impossible ever happened, falling through to the form beats rendering a card whose primary
    // action cannot fire.
    // Display label only — the persisted `PLAN-GROUP` value is the group id, not this label
    // (labels are marketing copy and change). Explicitly excluded for self-pay: `'self-pay'` IS an
    // entry in SUPPORTED_PAYERS (label "I would like to self-pay"), so an unguarded lookup would
    // resolve to that string and — if a future branch ever rendered `planLabel` for a self-pay
    // result — a member could read "We checked your I would like to self-pay…". Self-pay never
    // checked a plan at all, so the label must be absent, not merely unmatched.
    const planLabel =
      lastInput && lastInput.payerId !== SELF_PAY_PAYER_ID
        ? SUPPORTED_PAYERS.find((p) => p.groupId === lastInput.groupId)?.label
        : undefined;
    view = (
      <EligibilityOutcome
        result={result}
        onEnroll={() => void enroll()}
        onRetry={retry}
        onReset={() => setResult(null)}
        onSelfPay={() => continueWithoutInsurance(lastInput)}
        {...(planLabel !== undefined ? { planLabel } : {})}
        {...(lastInput?.memberId !== undefined ? { memberId: lastInput.memberId } : {})}
      />
    );
  } else {
    view = <EligibilityForm onSubmit={(i) => void run(i)} submitting={submitting} />;
  }

  // Heading tracks the stage: form → coverage → enrollment → payment → booking → booked.
  const isBooking = showBooking;
  const isPayment = showPayment;
  let heading: string;
  let subhead = '';
  if (booked) {
    heading = "You're all set!";
    subhead = 'Your consult is confirmed — details are in your inbox.';
  } else if (isBooking) {
    heading = 'Schedule your consult';
    subhead = 'Pick a time for your first consult with a registered dietitian.';
  } else if (isPayment) {
    heading = 'Add a payment method';
    // A copay member owes a copay; a self-pay member (chosen self-pay OR an out-of-pocket result,
    // where no plan covers the visit at all) owes the full consult fee, not a copay — telling them
    // to "save a card for your copay" would be false. Keep the reassurance clause in both: it's
    // true either way and is what stops people abandoning the form here.
    const hasCopay = result !== null && result.kind === 'eligible' && result.costPath === 'copay';
    subhead = hasCopay
      ? "Save a card for your copay — you won't be charged until after your consult."
      : "Save a card for your consult fee — you won't be charged until after your consult.";
  } else if (enrolling) {
    heading = 'Enrolling you';
    subhead = 'Setting up your care — one moment.';
  } else if (enrollResult !== null) {
    // blocked | error | booking_unavailable — the outcome card carries the specific message.
    heading = 'Enrollment';
  } else if (result !== null && result.kind === 'existing_member') {
    heading = "You're already enrolled";
  } else if (result !== null) {
    heading = 'Your coverage';
    subhead = "Here's what we found for your plan.";
  } else {
    heading = 'Check your eligibility';
    subhead = 'Confirm your coverage in a couple of minutes — no card required.';
  }

  return (
    // px/py live here rather than on AppShell's <main>: the landing page needs full-bleed colour
    // bands, so the shell no longer constrains its children.
    <div
      className={`mx-auto flex w-full flex-col gap-7 px-6 py-10 ${isBooking ? 'max-w-[52rem]' : 'max-w-[34rem]'}`}
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-[var(--forest)]">{heading}</h1>
        {subhead !== '' && <p className="text-[var(--ink-soft)]">{subhead}</p>}
      </div>
      {isBooking ? (
        view
      ) : (
        <div className="nrx-card rounded-[var(--radius-lg)] p-6 sm:p-8">{view}</div>
      )}
    </div>
  );
}
